import React, { useEffect, useRef, useState, useMemo } from 'react';
import * as d3 from 'd3';
import { getProjectWorktimeMonthlyStatisticsAPI } from '@/services/apis/dashboard';
import { useRequest } from "ahooks"
import { stringToFixColor } from "@/utils/color"
import { Spin } from "antd"

// 数据类型定义
interface MonthlyData {
    month: string; // 格式: "2024-07"
    // 各专业工时数
    backend: number;
    frontend: number;
    design: number;
    qa: number;
    pm: number;
    // 总填报人数
    totalPersonCount: number;
}

// 专业颜色配置
const PROFESSION_COLORS: Record<string, { color: string; label: string }> = {
    backend: { color: '#4f46e5', label: '后端开发' },
    frontend: { color: '#0ea5e9', label: '前端开发' },
    design: { color: '#8b5cf6', label: '设计' },
    qa: { color: '#10b981', label: '测试' },
    pm: { color: '#f59e0b', label: '项目管理' },
};

// 图例组件
const Legend: React.FC<{data: API.Project.DashboardProjectWorktime[]}> = ({data}) => {
    const dynamicProfessions = useMemo(() => {
        if (!data) {
            return [];
        }
        const uniqueProfessionsSet = new Set<string>()
        data.filter(v => v.data && v.month).forEach((item) => {
            item.data.forEach((d) => {
                uniqueProfessionsSet.add(d.name)
            })
        })
        return [...uniqueProfessionsSet].map((v) => ({ label: v, color: stringToFixColor(v) || '#000' }));
    }, [data]);
    return (
        <div className="flex flex-wrap gap-4 mb-6 justify-center">
            {dynamicProfessions.map(({ label, color }) => (
                <div key={label} className="flex items-center">
                    <div
                        className="w-4 h-4 mr-2 rounded"
                        style={{ backgroundColor: color }}
                    />
                    <span className="text-sm text-gray-700">{label}</span>
                </div>
            ))}
            <div className="flex items-center">
                <div className="w-6 h-0.5 bg-orange-500 mr-2" />
                <span className="text-sm text-gray-700">总填报人数</span>
            </div>
        </div>
    );
};

// 主图表组件
const ManpowerAnalysisChart: React.FC<{ projectID: number | string, departs: string[] }> = ({ projectID, departs }) => {
    const svgRef = useRef<SVGSVGElement>(null);
    const yAxisLeftRef = useRef<SVGSVGElement>(null);
    const yAxisRightRef = useRef<SVGSVGElement>(null);
    const chartContainerRef = useRef<HTMLDivElement>(null);
    const [data, setData] = useState<API.Project.DashboardProjectWorktime[]>([]);
    const [dimensions, setDimensions] = useState({ width: 800, height: 500 });
    const [tooltip, setTooltip] = useState<{
        visible: boolean;
        x: number;
        y: number;
        content: string;
    }>({ visible: false, x: 0, y: 0, content: '' });
    const tooltipTimerRef = useRef<number | null>(null);

    const { loading, run: fetchWorktimeData } = useRequest(getProjectWorktimeMonthlyStatisticsAPI, {
        manual: true,
        onSuccess: (res) => {
            setData(res.filter(v => v.data && v.month) || []);
            
            // 处理其他成功逻辑
        }
    })

    useEffect(() => {
        fetchWorktimeData(projectID, departs);
    }, [projectID, departs, fetchWorktimeData]);

    // 响应式调整尺寸
    useEffect(() => {
        const updateDimensions = () => {
            if (chartContainerRef.current) {
                const containerWidth = chartContainerRef.current.clientWidth;
                // 根据数据量计算最小宽度，每个月份至少120px
                const minChartWidth = Math.max(data.length * 60, 800);
                setDimensions({
                    width: Math.max(containerWidth, minChartWidth),
                    height: 500
                });
            }
        };

        updateDimensions();
        window.addEventListener('resize', updateDimensions);
        return () => window.removeEventListener('resize', updateDimensions);
    }, [data.length]);

    // 绘制图表
    useEffect(() => {
        if (!svgRef.current || !data.length || dimensions.width === 0) return;

        // 清除之前的图表
        d3.select(svgRef.current).selectAll('*').remove();
        d3.select(yAxisLeftRef.current).selectAll('*').remove();
        d3.select(yAxisRightRef.current).selectAll('*').remove();

        const svg = d3.select(svgRef.current);
        const svgLeft = d3.select(yAxisLeftRef.current);
        const svgRight = d3.select(yAxisRightRef.current);
        
        const { width, height } = dimensions;
        const margin = { top: 50, right: 10, bottom: 60, left: 10 };
        const yAxisWidth = 60;
        const chartWidth = width;
        const chartHeight = height - margin.top - margin.bottom;

        // 创建主图表区域
        const chart = svg
            .append('g')
            .attr('transform', `translate(${margin.left}, ${margin.top})`);

        // X轴比例尺（月份）
        const xScale = d3
            .scaleBand()
            .domain(data.map(d => d.month))
            .range([0, chartWidth])

        // 计算每个月的总工时，用于动态设置Y轴范围
        const maxTotalHours = d3.max(data, d => {
            return d.data.reduce((total, prof) => {
                return total + prof.value;
            }, 0);
        }) || 100;

        // 左侧Y轴比例尺（工时数）
        const yLeftScale = d3
            .scaleLinear()
            .domain([0, maxTotalHours * 1.1]) // 留出10%的空间
            .range([chartHeight, 0])
            .nice();

        const maxRightY = d3.max(data, d => d.clerkCount) || 20;
        // 右侧Y轴比例尺（填报人数）
        const yRightScale = d3
            .scaleLinear()
            .domain([0, maxRightY])
            .range([chartHeight, 0])
            .nice();

        // 绘制左侧Y轴（固定）
        const yLeftAxisGroup = svgLeft
            .append('g')
            .attr('transform', `translate(${yAxisWidth}, ${margin.top})`)
            .call(
                d3.axisLeft(yLeftScale)
                    .ticks(8)
            );

        yLeftAxisGroup.selectAll('text')
            .attr('class', 'text-xs text-gray-600');

        yLeftAxisGroup.selectAll('path, line')
            .attr('class', 'stroke-gray-300');

        // 左侧Y轴标签
        svgLeft
            .append('text')
            .attr('transform', 'rotate(-90)')
            .attr('y', 15)
            .attr('x', -(chartHeight / 2 + margin.top))
            .attr('text-anchor', 'middle')
            .attr('class', 'text-sm font-medium text-gray-700')
            .text('工时数');

        // 绘制右侧Y轴（固定）
        const yRightAxisGroup = svgRight
            .append('g')
            .attr('transform', `translate(0, ${margin.top})`)
            .call(
                d3.axisRight(yRightScale)
                    .ticks(6)
            );

        yRightAxisGroup.selectAll('text')
            .attr('class', 'text-xs text-gray-600');

        yRightAxisGroup.selectAll('path, line')
            .attr('class', 'stroke-gray-300');

        // 右侧Y轴标签
        svgRight
            .append('text')
            .attr('transform', `translate(${yAxisWidth / 2}, ${chartHeight / 2 + margin.top}) rotate(90)`)
            .attr('text-anchor', 'middle')
            .attr('class', 'text-sm font-medium text-gray-700')
            .text('填写工时的人数');

        // 添加X轴（在主SVG中）
        const xAxis = chart
            .append('g')
            .attr('transform', `translate(0, ${chartHeight})`)
            .call(
                d3.axisBottom(xScale)
                    .tickFormat(d => {
                        const dateStr = d as string;
                        return dateStr;
                    })
            );

        xAxis.selectAll('text')
            .attr('class', 'text-xs text-gray-600')
            .attr('dy', '1em')
            .attr('dx', '-0.5em')
            .style('text-anchor', 'middle');

        xAxis.selectAll('path, line')
            .attr('class', 'stroke-gray-300');

        // 添加X轴标题
        svg
            .append('text')
            .attr('x', width / 2)
            .attr('y', height - 10)
            .attr('text-anchor', 'middle')
            .attr('class', 'text-sm font-medium text-gray-700')
            .text('月份');

        // 添加图表标题
        // svg
        //     .append('text')
        //     .attr('x', width / 2)
        //     .attr('y', 30)
        //     .attr('text-anchor', 'middle')
        //     .attr('class', 'text-xl font-semibold text-gray-900')
        //     .text('人力投入分析图表');

        // 添加水平网格线（去掉最左侧的竖线）
        chart
            .append('g')
            .attr('class', 'grid')
            .call(
                d3.axisLeft(yLeftScale)
                    .tickSize(-chartWidth)
                    .tickFormat(() => '')
            )
            .selectAll('line')
            .attr('class', 'stroke-gray-200 stroke-1')
            .attr('stroke-dasharray', '3,3');
        
        // 隐藏Y轴的主轴线（避免黑线）
        chart.select('.grid path').remove();

        // 从数据中提取动态的专业列表
        const professionSet = new Set<string>();
        data.forEach(d => {
            d.data.forEach(prof => {
                professionSet.add(prof.name);
            });
        });
        const dynamicProfessions = Array.from(professionSet);
        
        // 构建动态专业颜色映射
        const colorPalette = ['#4f46e5', '#0ea5e9', '#8b5cf6', '#10b981', '#f59e0b', '#ef4444', '#ec4899', '#6366f1'];
        const professionMap: Record<string, { color: string; label: string }> = {};
        dynamicProfessions.forEach((prof, index) => {
            professionMap[prof] = {
                color: stringToFixColor(prof),
                label: prof
            };
        });

        // 先按月份绘制
        data.forEach((monthData, monthIndex) => {
            // 对每个月的专业数据进行堆叠绘制
            monthData.data.forEach((profData, profIndex) => {
                // 计算当前专业之前的累积高度
                let cumulativeHeight = 0;
                for (let i = 0; i < profIndex; i++) {
                    cumulativeHeight += monthData.data[i].value;
                }
                
                chart
                    .append('rect')
                    .attr('class', `bar bar-${profData.name} transition-all duration-200`)
                    .attr('x', (xScale(monthData.month) || 0) + xScale.bandwidth() / 4)
                    .attr('y', yLeftScale(cumulativeHeight + profData.value))
                    .attr('width', xScale.bandwidth() / 2)
                    .attr('height', chartHeight - yLeftScale(profData.value))
                    .attr('fill', professionMap[profData.name]?.color || '#888')
                    .attr('rx', 2)
                    .on('mouseenter', function (event) {
                        if (tooltipTimerRef.current) {
                            clearTimeout(tooltipTimerRef.current);
                            tooltipTimerRef.current = null;
                        }
                        
                        // 构建包含所有专业数据的提示内容
                        let content = `<div class="font-semibold text-xs text-white mb-1">${monthData.month}</div>`;
                        monthData.data.forEach(prof => {
                            const label = professionMap[prof.name]?.label || prof.name;
                            content += `<div class="text-white text-xs mb-1">${label}: ${prof.value}</div>`;
                        });

                        d3.select(this)
                            .classed('opacity-80', true)
                            .attr('filter', 'brightness(1.1)');

                        setTooltip({
                            visible: true,
                            x: event.pageX + 10,
                            y: event.pageY - 10,
                            content: content
                        });
                    })
                    .on('mouseleave', function () {
                        d3.select(this)
                            .classed('opacity-80', false)
                            .attr('filter', null);

                        tooltipTimerRef.current = window.setTimeout(() => {
                            setTooltip(prev => ({ ...prev, visible: false }));
                        }, 100);
                    });
            });
        });

        // 绘制柱状图的文字标签，按月绘制，用g包含
        data.forEach(d => {
            // 为每个月创建一个g元素
            const monthGroup = chart
                .append('g')
                .attr('class', `month-labels month-${d.month}`)
                .attr('transform', `translate(${(xScale(d.month) || 0) + xScale.bandwidth() / 2}, 0)`);
            
            const baseY = yLeftScale(0);
            let maxTextPos = baseY;

            // 为每个专业绘制标签
            d.data.forEach((profData, index) => {
                const currentValue = profData.value;
                const barHeight = chartHeight - yLeftScale(currentValue);
                
                // 计算累积高度
                let cumulativeHeight = 0;
                for (let i = 0; i < index; i++) {
                    cumulativeHeight += d.data[i].value;
                }
                
                // 计算柱子中心点高度
                const middleHeight = cumulativeHeight + currentValue / 2;
                const middleY = yLeftScale(middleHeight);
                
                let labelY: number;
                
                if (middleY < maxTextPos - 13) {
                    labelY = middleY;
                } else {
                    labelY = maxTextPos - 13;
                }

                maxTextPos = labelY - 13;
                
                // 创建文本元素
                const text = monthGroup
                    .append('text')
                    .attr('class', `label-${profData.name} text-xs font-medium text-white`)
                    .attr('y', labelY)
                    .attr('text-anchor', 'middle')
                    .attr('dominant-baseline', 'middle')
                    .style('pointer-events', 'none')
                    .style('z-index', 10);
                
                // 添加专业名称
                text
                    .append('tspan')
                    .attr('x', 0)
                    .attr('dy', '-0.5em')
                    .text(professionMap[profData.name]?.label || profData.name);
                
                // 添加工时数
                text
                    .append('tspan')
                    .attr('x', 0)
                    .attr('dy', '1em')
                    .text(currentValue);
            });
        });

        // 创建折线图生成器
        const lineGenerator = d3
            .line<API.Project.DashboardProjectWorktime>()
            .x(d => (xScale(d.month) || 0) + xScale.bandwidth() / 2)
            .y(d => yRightScale(d.clerkCount || 0))
            .curve(d3.curveMonotoneX);

        // 绘制折线
        chart
            .append('path')
            .datum(data)
            .attr('fill', 'none')
            .attr('class', 'stroke-orange-500 stroke-3')
            .attr('d', lineGenerator);

        // 绘制折线上的圆点
        const dots = chart
            .selectAll('.person-dot')
            .data(data)
            .enter()
            .append('circle')
            .attr('class', 'person-dot transition-all duration-200')
            .attr('cx', d => (xScale(d.month) || 0) + xScale.bandwidth() / 2)
            .attr('cy', d => yRightScale(d.clerkCount || 0))
            .attr('r', 5)
            .attr('class', 'stroke-white fill-orange-500 stroke-2')
            .on('mouseover', function (event, d) {
                const date = d.month;

                d3.select(this)
                    .attr('r', 7);

                setTooltip({
                    visible: true,
                    x: event.pageX + 10,
                    y: event.pageY - 10,
                    content: `
            <div class="font-semibold text-xs text-white mb-1">${date}</div>
            <div class="text-white font-medium text-xs">填报人数: ${d.clerkCount || 0}</div>
          `
                });
            })
            .on('mouseout', function () {
                d3.select(this)
                    .attr('r', 5);

                setTooltip(prev => ({ ...prev, visible: false }));
            });

        // 在圆点上方添加人数标签
        chart
            .selectAll('.person-label')
            .data(data)
            .enter()
            .append('text')
            .attr('class', 'person-label text-xs font-medium')
            .attr('x', d => (xScale(d.month) || 0) + xScale.bandwidth() / 2)
            .attr('y', d => yRightScale(d.clerkCount || 0) - 10) // 位置在圆点上方
            .attr('text-anchor', 'middle')
            .attr('dominant-baseline', 'middle')
            .attr('fill', '#f97316')
            .text(d => d.clerkCount || 0);

    }, [data, dimensions]);

    return (
        <div className="px-4">
            <div className="mx-auto">
                {/* 头部 */}

                <h3 className="text-sm font-semibold text-gray-700 mb-4">人力投入分析看板</h3>
                <Spin spinning={loading}>
                    {/* 图表容器 */}
                    {data.length > 0 && (
                        <div className="bg-white">
                            <Legend data={data} />

                            <div className="flex w-full">
                                {/* 左侧固定Y轴 */}
                                <div
                                    className="sticky left-0 z-10 bg-white shrink-0"
                                    style={{ width: '60px', height: dimensions.height }}
                                >
                                    <svg
                                        ref={yAxisLeftRef}
                                        width="60"
                                        height={dimensions.height}
                                    />
                                </div>

                                {/* 中间可滚动图表区 */}
                                <div
                                    ref={chartContainerRef}
                                    className="overflow-x-auto grow"
                                >
                                    <svg
                                        ref={svgRef}
                                        width={dimensions.width}
                                        height={dimensions.height}
                                        className="mx-auto"
                                    />
                                </div>

                                {/* 右侧固定Y轴 */}
                                <div
                                    className="sticky right-0 z-10 bg-white shrink-0"
                                    style={{ width: '60px', height: dimensions.height }}
                                >
                                    <svg
                                        ref={yAxisRightRef}
                                        width="60"
                                        height={dimensions.height}
                                    />
                                </div>
                            </div>
                        </div>
                    )}
                </Spin>
                {!loading && data.length <= 0 && <div className="text-center text-xs text-gray-500 h-[200px] flex items-center justify-center">暂无数据</div>}
            </div>

            {/* 工具提示 */}
            {tooltip.visible && (
                <div
                    className="fixed z-50 bg-gray-900 text-white p-3 rounded-lg shadow-lg text-sm pointer-events-none"
                    style={{
                        left: `${tooltip.x - window.scrollX}px`,
                        top: `${tooltip.y - window.scrollY}px`,
                        maxWidth: '200px',
                    }}
                    dangerouslySetInnerHTML={{ __html: tooltip.content }}
                />
            )}
        </div>
    );
};

export default ManpowerAnalysisChart;